// İSG uzmanı portalı — sunum yardımcıları. Sunucu UTC ISO 8601 gönderir (api-contract
// §4); yerelleştirme sunum katmanındadır. Geçersiz/boş değerde tire döner.

/** UTC ISO tarihini aktif kültüre göre kısa tarih olarak biçimlendirir. */
export function formatDate(iso: string | null | undefined, culture: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { dateStyle: 'medium' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/**
 * UTC ISO tarih-saatini aktif kültüre + kullanıcının yerel saatine göre YALNIZ SAAT olarak
 * biçimlendirir (Güvenlik masası rayı/gün listesi — kompakt satırlar, tam tarih gereksiz
 * çünkü liste zaten tek güne kapsanmış, api-contract §29.5). Geçersiz/boş değerde tire döner.
 */
export function formatTime(iso: string | null | undefined, culture: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { timeStyle: 'short' }).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

/**
 * UTC ISO tarih aralığını aktif kültüre göre KISA bir aralık olarak biçimlendirir (S32d
 * `docs/reviews/S32d-verdict-and-detail.md` §1.4 madde 2 — karar kartının izin satırları).
 * `formatDateTime` ile iki uç noktayı ayrı ayrı basmak (`17 Ağu 2026 20:20 – 31 Ağu 2026
 * 20:20`, 37 karakter) bir izin PENCERESİ için gürültüdür — saatler bir kapı kararını
 * DEĞİŞTİRMEZ, tekrarlanan yıl da öyle. `Intl.DateTimeFormat.prototype.formatRange` platformun
 * kendisindedir (yeni bağımlılık YOK) ve ORTAK bileşenleri kültüre göre daraltır: tr
 * `17–31 Ağu 2026`, de `17.–31. Aug. 2026`, en `17–31 Aug 2026` (15 karakter). Geçersiz/boş
 * değerde tire döner; `formatRange` platform desteklemiyorsa (çok eski tarayıcı) iki
 * `formatDate` çağrısına düşer.
 */
export function formatDateRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  culture: string,
): string {
  if (!startIso || !endIso) {
    return '—';
  }
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { dateStyle: 'medium' }).formatRange(start, end);
  } catch {
    return `${formatDate(startIso, culture)} – ${formatDate(endIso, culture)}`;
  }
}

/**
 * İki anın YEREL takvim günü farkı (`hedef gün - referans gün`, saat dilimini SAYMAZ). Ham ms
 * farkı (`iso.getTime() - Date.now()) / DAY_MS`) 24 saatlik bloklar sayar, takvim günü DEĞİL —
 * saat 14:00'te ertesi gün 08:00'de başlayan bir izin (18 saat sonra) yanlışlıkla "bugün
 * başlıyor" görünür. Bu fonksiyon her iki tarihin YEREL yıl/ay/gün bileşenini karşılaştırır;
 * 0 = bugün, 1 = yarın, negatif = geçmiş. `RiskItemsPanel` (DÖF gecikme) ve `ActionItemsPanel`
 * (dashboard "bugün/yarın başlıyor") ortak kullanır.
 */
export function calendarDayDiff(iso: string, reference: Date = new Date()): number {
  const target = new Date(iso);
  const DAY_MS = 24 * 60 * 60 * 1000;
  const startOfTarget = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const startOfReference = Date.UTC(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
  );
  return Math.round((startOfTarget - startOfReference) / DAY_MS);
}

/**
 * Sunucunun `'YYYY-MM-DD'` TAKVİM GÜNÜ dizesini (bir UTC ANI DEĞİL — §34.1
 * `permitsCreatedPerDay.date`, §28.3 `riskDays` ile aynı desen) yerel bir `Date`'e çevirir.
 * `new Date(iso + 'T00:00:00Z')` dizeyi UTC gece yarısı bir ANA bağlar; görüntüleyenin saat
 * dilimi UTC'nin BATISINDA ise (İstanbul dışı bir tarayıcı) `Intl.DateTimeFormat` varsayılan
 * yerel saat diliminde biçimlendirirken bir gün GERİYE kayar — sunucunun `Europe/Istanbul`
 * hesabıyla ÇELİŞİR. y/m/d bileşenlerini ayrı okuyup yerel `Date(y, m-1, d)` kurmak,
 * görüntüleyenin saat dilimi ne olursa olsun AYNI takvim gününü korur. Geçersiz girdide
 * `null` döner (çağıran tire ile karşılar).
 */
export function parseCalendarDate(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Baş harfler — karar kartının avatar döşemesi (S32c §3.3, `Avatar.tsx`). Türkçe büyütme
 * kuralı SABİT `'tr'` — ad Türkçe VERİDİR, arayüz DİLİYLE İLGİSİZ (bkz. `apps/mobile-supplier/
 * src/lib/format.ts:193-206`'nın `initials()`'ı: o arayüz diline göre büyütür, ki KENDİ
 * profilini okuyan kullanıcı için doğrudur; burada YANLIŞ olurdu — bakan güvenlik görevlisinin
 * locale'i, adı taşınan personelin verisiyle İLGİSİZDİR). "irem" → "İ", "I" DEĞİL.
 * Boş/tanınmayan ad → boş dize (tile'da GLİF yok — "?" bir kimlik iddiası taşır, §3.5).
 */
export function initials(fullName: string | null | undefined): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  const raw = `${first.charAt(0)}${last.charAt(0)}`;
  try {
    return raw.toLocaleUpperCase('tr');
  } catch {
    return raw.toUpperCase();
  }
}

/**
 * UTC ISO tarih-saatini aktif kültüre + **kullanıcının yerel saatine** göre biçimlendirir
 * (api-contract §4: sunucu daima UTC gönderir; yerelleştirme sunum katmanındadır). Ziyaret
 * giriş/çıkış anları gibi saat gerektiren alanlarda kullanılır (giriş saati acil durumda
 * kritik). Geçersiz/boş değerde tire döner.
 */
export function formatDateTime(iso: string | null | undefined, culture: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { dateStyle: 'medium', timeStyle: 'short' }).format(
      date,
    );
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
}
